<template>
  <div class="inscricoes-lista">
    <div class="page-header">
      <h1 class="page-title">
        <i class="fas fa-file-alt"></i>
        Gestão de Inscrições
      </h1>
      <p class="page-subtitle">Gerencie todas as inscrições do sistema</p>
    </div>

    <!-- Filtros -->
    <div class="filters-card">
      <div class="filters-row">
        <div class="filter-group">
          <label>Buscar</label>
          <input 
            v-model="filters.search" 
            type="text" 
            placeholder="Nome, CPF ou E-mail do candidato"
            class="form-control"
            @input="debounceSearch"
          />
        </div>
        
        <div class="filter-group">
          <label>Concurso</label>
          <select v-model="filters.concurso_id" class="form-control" @change="onConcursoChange">
            <option value="">Todos os concursos</option>
            <option v-for="concurso in concursos" :key="concurso.id" :value="concurso.id">
              {{ concurso.prefeitura }} - {{ concurso.titulo }}
            </option>
          </select>
        </div>

        <div class="filter-group">
          <label>Cargo/Vaga</label>
          <select v-model="filters.vaga_id" class="form-control" @change="applyFilters" :disabled="!filters.concurso_id">
            <option value="">Todos os cargos</option>
            <option v-for="vaga in vagas" :key="vaga.id" :value="vaga.id">
              {{ vaga.cargo }}
            </option>
          </select>
        </div>

        <div class="filter-group">
          <label>Status</label>
          <select v-model="filters.status" class="form-control" @change="applyFilters">
            <option value="">Todos</option>
            <option value="A">Ativa</option>
            <option value="C">Cancelada</option>
            <option value="D">Deferida</option>
            <option value="I">Indeferida</option>
          </select>
        </div>

        <div class="filter-group">
          <label>Situação Pagamento</label>
          <select v-model="filters.situacao_pagamento" class="form-control" @change="applyFilters">
            <option value="">Todas</option>
            <option value="Paga">Pagas</option>
            <option value="Isenta">Isentas</option>
            <option value="Pendente">Pendentes</option>
          </select>
        </div>

        <div class="filter-group">
          <label>Data Início</label>
          <input 
            v-model="filters.data_inicio" 
            type="date" 
            class="form-control"
            @change="applyFilters"
          />
        </div>

        <div class="filter-group">
          <label>Data Fim</label>
          <input 
            v-model="filters.data_fim" 
            type="date" 
            class="form-control"
            @change="applyFilters"
          />
        </div>

        <div class="filter-actions">
          <button @click="clearFilters" class="btn btn-secondary">
            <i class="fas fa-times"></i>
            Limpar
          </button>
          <button @click="exportData" class="btn btn-success">
            <i class="fas fa-download"></i>
            Exportar
          </button>
        </div>
      </div>
    </div>

    <!-- Estatísticas -->
    <div class="stats-cards">
      <div class="stat-card">
        <div class="stat-icon">
          <i class="fas fa-file-alt"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ estatisticas.total_inscricoes || 0 }}</div>
          <div class="stat-label">Total de Inscrições</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon">
          <i class="fas fa-check-circle"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ estatisticas.inscricoes_ativas || 0 }}</div>
          <div class="stat-label">Inscrições Ativas</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon">
          <i class="fas fa-times-circle"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ estatisticas.inscricoes_canceladas || 0 }}</div>
          <div class="stat-label">Inscrições Canceladas</div>
        </div>
      </div>
    </div>

    <!-- Tabela de Inscrições -->
    <div class="table-card">
      <div v-if="loading" class="loading-state">
        <i class="fas fa-spinner fa-spin"></i>
        <p>Carregando inscrições...</p>
      </div>

      <div v-else-if="inscricoes.length === 0" class="empty-state">
        <i class="fas fa-file-alt"></i>
        <h3>Nenhuma inscrição encontrada</h3>
        <p>Não há inscrições que correspondam aos filtros aplicados.</p>
      </div>

      <div v-else>
        <div class="table-responsive">
          <table class="table table-hover">
            <thead>
              <tr>
                <th @click="sortByField('id')" class="sortable col-id">
                  ID <i class="fas fa-sort"></i>
                </th>
                <th class="col-candidato">Candidato</th>
                <th class="col-cpf">CPF</th>
                <th class="col-concurso">Concurso</th>
                <th class="col-cargo">Cargo</th>
                <th class="col-data">Data Inscrição</th>
                <th class="col-status">Status</th>
                <th class="col-situacao">Situação</th>
                <th class="col-acoes">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="inscricao in inscricoes" :key="inscricao.id">
                <td>{{ inscricao.id }}</td>
                <td>
                  <div class="candidate-info">
                    <strong>{{ inscricao.cadastro?.nome }}</strong>
                    <small class="text-muted">{{ inscricao.cadastro?.email }}</small>
                  </div>
                </td>
                <td>{{ formatCPF(inscricao.cadastro?.cpf) }}</td>
                <td>
                  <div class="concurso-info">
                    <span
                      class="concurso-text"
                      :title="`${inscricao.concurso?.prefeitura || ''} - ${inscricao.concurso?.titulo || ''}`"
                    >
                      {{ inscricao.concurso?.id }} - 
                      {{ inscricao.concurso?.prefeitura }}
                    </span>
                  </div>
                </td>
                <td>{{ inscricao.vaga?.cargo }}</td>
                <td>{{ formatDate(inscricao.created_at) }}</td>
                <td>
                  <span :class="getStatusClass(inscricao.status)">
                    {{ getStatusText(inscricao.status) }}
                  </span>
                </td>
                <td>
                  <span :class="getSituacaoClass(inscricao.situacao || '')">
                    {{ inscricao.situacao }}
                  </span>
                </td>
                <td class="actions">
                  <button 
                    @click="viewDetails(inscricao)" 
                    class="btn btn-sm btn-info"
                    title="Ver detalhes"
                  >
                    <i class="fas fa-eye"></i>
                  </button>
                  <button 
                    @click="toggleStatus(inscricao)" 
                    :class="inscricao.status === 'A' ? 'btn btn-sm btn-warning' : 'btn btn-sm btn-success'"
                    :title="inscricao.status === 'A' ? 'Cancelar' : 'Reativar'"
                  >
                    <i :class="inscricao.status === 'A' ? 'fas fa-ban' : 'fas fa-check'"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Paginação -->
        <div class="pagination-wrapper">
          <nav>
            <ul class="pagination">
              <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <button @click="changePage(currentPage - 1)" class="page-link">Anterior</button>
              </li>
              <li 
                v-for="page in visiblePages" 
                :key="page" 
                class="page-item" 
                :class="{ active: page === currentPage }"
              >
                <button @click="changePage(page)" class="page-link">{{ page }}</button>
              </li>
              <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                <button @click="changePage(currentPage + 1)" class="page-link">Próxima</button>
              </li>
            </ul>
          </nav>
          <div class="pagination-info">
            Mostrando {{ (currentPage - 1) * perPage + 1 }} a {{ Math.min(currentPage * perPage, totalItems) }} de {{ totalItems }} inscrições
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Detalhes da Inscrição -->
    <div
      v-if="showDetailsModal && inscricaoSelecionada"
      class="modal-overlay"
      @click="closeDetailsModal"
    >
      <div
        class="modal-content large"
        @click.stop
        role="dialog"
        aria-modal="true"
        :aria-label="`Detalhes da Inscrição #${inscricaoSelecionada.id}`"
      >
        <div class="modal-header">
          <h4 class="modal-title">
            <i class="fas fa-file-alt"></i>
            Detalhes da Inscrição #{{ inscricaoSelecionada.id }}
          </h4>
          <button @click="closeDetailsModal" class="modal-close" aria-label="Fechar modal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <!-- Informações do Candidato -->
          <div class="details-section">
            <h5 class="section-title">
              <i class="fas fa-user"></i>
              Dados do Candidato
            </h5>
            <div class="details-grid">
              <div class="detail-item">
                <label>Nome</label>
                <span>{{ inscricaoSelecionada.cadastro?.nome }}</span>
              </div>
              <div class="detail-item">
                <label>CPF</label>
                <span>{{ formatCPF(inscricaoSelecionada.cadastro?.cpf) }}</span>
              </div>
              <div class="detail-item">
                <label>E-mail</label>
                <span>{{ inscricaoSelecionada.cadastro?.email }}</span>
              </div>
              <div class="detail-item">
                <label>Telefone</label>
                <span>{{ inscricaoSelecionada.cadastro?.telefone_celular || '-' }}</span>
              </div>
            </div>
          </div>

          <!-- Informações do Concurso -->
          <div class="details-section">
            <h5 class="section-title">
              <i class="fas fa-clipboard-list"></i>
              Dados do Concurso
            </h5>
            <div class="details-grid">
              <div class="detail-item">
                <label>Prefeitura</label>
                <span>{{ inscricaoSelecionada.concurso?.prefeitura }}</span>
              </div>
              <div class="detail-item">
                <label>Título</label>
                <span>{{ inscricaoSelecionada.concurso?.titulo }}</span>
              </div>
              <div class="detail-item">
                <label>Cargo</label>
                <span>{{ inscricaoSelecionada.vaga?.cargo }}</span>
              </div>
              <div class="detail-item">
                <label>Nível</label>
                <span>{{ inscricaoSelecionada.vaga?.nivel || '-' }}</span>
              </div>
            </div>
          </div>

          <!-- Status da Inscrição -->
          <div class="details-section">
            <h5 class="section-title">
              <i class="fas fa-info-circle"></i>
              Status da Inscrição
            </h5>
            <div class="details-grid">
              <div class="detail-item">
                <label>Status</label>
                <span :class="getStatusClass(inscricaoSelecionada.status)">
                  {{ getStatusText(inscricaoSelecionada.status) }}
                </span>
              </div>
              <div class="detail-item">
                <label>Situação</label>
                <span :class="getSituacaoClass(inscricaoSelecionada.situacao || '')">
                  {{ inscricaoSelecionada.situacao || '-' }}
                </span>
              </div>
              <div class="detail-item">
                <label>Data da Inscrição</label>
                <span>{{ formatDate(inscricaoSelecionada.created_at) }}</span>
              </div>
              <div class="detail-item">
                <label>Isenção Solicitada</label>
                <span :class="inscricaoSelecionada.isencao_solicitada ? 'badge badge-success' : 'badge badge-secondary'">
                  {{ inscricaoSelecionada.isencao_solicitada ? 'Sim' : 'Não' }}
                </span>
              </div>
            </div>
          </div>

          <!-- Informações Especiais -->
          <div class="details-section">
            <h5 class="section-title">
              <i class="fas fa-wheelchair"></i>
              Necessidades Especiais
            </h5>
            <div class="details-grid">
              <div class="detail-item full-width">
                <label>Possui Deficiência</label>
                <span :class="inscricaoSelecionada.possui_deficiencia ? 'badge badge-info' : 'badge badge-secondary'">
                  {{ inscricaoSelecionada.possui_deficiencia ?? 'Não' }}
                </span>
              </div>
              <div v-if="inscricaoSelecionada.possui_deficiencia" class="detail-item full-width">
                <label>Detalhes da Deficiência</label>
                <span>{{ inscricaoSelecionada.detalhe_deficiencia || '-' }}</span>
              </div>
              <div v-if="inscricaoSelecionada.url_arquivo_deficiencia" class="detail-item full-width">
                <label>Arquivo de Comprovação</label>
                <a :href="inscricaoSelecionada.url_arquivo_deficiencia" target="_blank" class="link-file large">
                  <i class="fas fa-file-download"></i>
                  Baixar Arquivo
                </a>
              </div>

              <div class="detail-item full-width">
                <label>Possui Condição Especial</label>
                <span :class="inscricaoSelecionada.possui_condicao_especial ? 'badge badge-info' : 'badge badge-secondary'">
                  {{ inscricaoSelecionada.possui_condicao_especial ?? 'Não' }}
                </span>
              </div>
              <div v-if="inscricaoSelecionada.possui_condicao_especial" class="detail-item full-width">
                <label>Detalhes da Condição Especial</label>
                <span>{{ inscricaoSelecionada.detalhe_condicao_especial || '-' }}</span>
              </div>
              <div v-if="inscricaoSelecionada.url_arquivo_condicao_especial" class="detail-item full-width">
                <label>Arquivo de Comprovação</label>
                <a :href="inscricaoSelecionada.url_arquivo_condicao_especial" target="_blank" class="link-file large">
                  <i class="fas fa-file-download"></i>
                  Baixar Arquivo
                </a>
              </div>

              <div class="detail-item full-width">
                <label>Entrega de Títulos</label>
                <span :class="inscricaoSelecionada.entrega_titulos ? 'badge badge-success' : 'badge badge-secondary'">
                  {{ inscricaoSelecionada.entrega_titulos ? 'Sim' : 'Não' }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeDetailsModal" class="btn btn-secondary">
            Fechar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'InscricoesLista',
  data() {
    return {
      loading: false,
      inscricoes: [],
      concursos: [],
      vagas: [],
      estatisticas: {},
      filters: {
        search: '',
        concurso_id: '',
        vaga_id: '',
        status: '',
        situacao_pagamento: '',
        data_inicio: '',
        data_fim: ''
      },
      sortBy: 'created_at',
      sortOrder: 'desc',
      currentPage: 1,
      perPage: 15,
      totalItems: 0,
      totalPages: 0,
      searchTimeout: null,
      showDetailsModal: false,
      inscricaoSelecionada: null
    }
  },
  computed: {
    visiblePages() {
      const pages = []
      const start = Math.max(1, this.currentPage - 2)
      const end = Math.min(this.totalPages, this.currentPage + 2)
      for (let i = start; i <= end; i++) pages.push(i)
      return pages
    }
  },
  mounted() {
    this.loadConcursos()
    this.loadInscricoes()
    this.loadEstatisticas()
    window.addEventListener('keydown', this.onKeydown)
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    async loadConcursos() {
      try {
        const response = await axios.get('/ps/concurso/admin/todos')
        this.concursos = response.data
      } catch (error) {
        console.error('Erro ao carregar concursos:', error)
      }
    },

    async loadVagas(concursoId) {
      if (!concursoId) {
        this.vagas = []
        return
      }
      
      try {
        const response = await axios.get(`/ps/concurso/${concursoId}/vagas`)
        this.vagas = response.data || []
      } catch (error) {
        console.error('Erro ao carregar vagas:', error)
        this.vagas = []
      }
    },

    onConcursoChange() {
      // Limpar filtro de vaga quando o concurso mudar
      this.filters.vaga_id = ''
      // Carregar vagas do concurso selecionado
      this.loadVagas(this.filters.concurso_id)
      // Aplicar filtros
      this.applyFilters()
    },

    async loadInscricoes() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          per_page: this.perPage,
          sort_by: this.sortBy,
          sort_order: this.sortOrder,
          ...this.filters
        }
        const response = await axios.get('/ps/inscricao/admin/todas', { params })
        this.inscricoes = response.data.data || []
        this.totalItems = response.data.total || 0
        this.totalPages = response.data.last_page || 0
      } catch (error) {
        console.error('Erro ao carregar inscrições:', error)
        this.$sweet?.showError?.('Erro ao carregar inscrições')
      } finally {
        this.loading = false
      }
    },

    async loadEstatisticas() {
      try {
        const params = { ...this.filters }
        const response = await axios.get('/ps/inscricao/admin/estatisticas', { params })
        this.estatisticas = response.data || {}
      } catch (error) {
        console.error('Erro ao carregar estatísticas:', error)
      }
    },
    
    debounceSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1
        this.applyFilters()
      }, 500)
    },

    applyFilters() {
      this.currentPage = 1
      this.loadInscricoes()
      this.loadEstatisticas()
    },

    clearFilters() {
      this.filters = {
        search: '',
        concurso_id: '',
        vaga_id: '',
        status: '',
        situacao_pagamento: '',
        data_inicio: '',
        data_fim: ''
      }
      this.vagas = []
      this.currentPage = 1
      this.loadInscricoes()
      this.loadEstatisticas()
    },

    sortByField(field) {
      if (this.sortBy === field) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortBy = field
        this.sortOrder = 'asc'
      }
      this.loadInscricoes()
    },

    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page
        this.loadInscricoes()
      }
    },

    async viewDetails(inscricao) {
      this.inscricaoSelecionada = inscricao
      this.showDetailsModal = true
      
      // Carregar dados completos da inscrição se necessário
      if (!inscricao.detalhes_carregados) {
        await this.loadInscricaoDetalhes(inscricao.id)
      }
    },

    async loadInscricaoDetalhes(inscricaoId) {
      try {
        const response = await axios.get(`/ps/inscricao/admin/${inscricaoId}`)
        this.inscricaoSelecionada = {
          ...this.inscricaoSelecionada,
          ...response.data,
          detalhes_carregados: true
        }
      } catch (error) {
        console.error('Erro ao carregar detalhes da inscrição:', error)
        this.$sweet?.showError?.('Erro ao carregar detalhes da inscrição')
      }
    },

    closeDetailsModal() {
      this.showDetailsModal = false
      this.inscricaoSelecionada = null
    },

    onKeydown(e) {
      if (this.showDetailsModal && e.key === 'Escape') {
        this.closeDetailsModal()
      }
    },

    async toggleStatus(inscricao) {
      try {
        const newStatus = inscricao.status === 'A' ? 'C' : 'A'
        const action = newStatus === 'A' ? 'reativar' : 'cancelar'
        
        const { value } = await this.$sweet.showConfirm(
          'Confirmar Ação',
          `Deseja realmente ${action} a inscrição #${inscricao.id}?`
        )

        if (value) {
          await axios.put(`/ps/inscricao/admin/${inscricao.id}/status`, {
            status: newStatus
          })

          this.$sweet.showSuccess(`Inscrição ${action}da com sucesso!`)
          this.loadInscricoes()
          this.loadEstatisticas()
        }
      } catch (error) {
        console.error('Erro ao alterar status:', error)
        this.$sweet.showError('Erro ao alterar status da inscrição')
      }
    },

    async exportData() {
      try {
        this.$sweet.showInfo('Gerando arquivo Excel...')
        
        const response = await axios.get('/ps/inscricao/admin/exportar-excel', {
          params: this.filters,
          responseType: 'blob'
        })
        
        // Criar download do arquivo
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `inscricoes_${new Date().getTime()}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        
        this.$sweet.showSuccess('Arquivo exportado com sucesso!')
      } catch (error) {
        console.error('Erro ao exportar dados:', error)
        this.$sweet.showError('Erro ao exportar dados')
      }
    },

    formatCPF(cpf) {
      if (!cpf) return '-'
      return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    },

    formatDate(date) {
      if (!date) return '-';
      
      try {
        // Se a data vier apenas com a data (sem hora), usar diretamente
        if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = date.split('-');
          return `${day}/${month}/${year}`;
        }
        
        // Tratar formato do banco: YYYY-MM-DD HH:mm:ss (com espaço)
        if (typeof date === 'string') {
          let dateString = date.replace('Z', '');
          
          // Se tiver formato com espaço (formato do banco: YYYY-MM-DD HH:mm:ss)
          if (dateString.match(/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/)) {
            const [datePart] = dateString.split(' ');
            const [year, month, day] = datePart.split('-');
            return `${day}/${month}/${year}`;
          }
          
          // Para datas ISO com timezone, remover o 'Z' e tratar como local
          if (dateString.includes('T')) {
            dateString = dateString.replace('Z', '');
            if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
              const [datePart] = dateString.split('T');
              const [year, month, day] = datePart.split('-');
              return `${day}/${month}/${year}`;
            }
          }
        }
        
        // Criar data e usar métodos locais
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) return '-';
        
        const dia = String(dateObj.getDate()).padStart(2, '0');
        const mes = String(dateObj.getMonth() + 1).padStart(2, '0');
        const ano = dateObj.getFullYear();
        return `${dia}/${mes}/${ano}`;
      } catch (error) {
        return '-';
      }
    },

    getStatusClass(status) {
      switch (status) {
        case 'A':
          return 'badge badge-info'
        case 'C':
          return 'badge badge-secondary'
        case 'D':
          return 'badge badge-success'
        case 'I':
          return 'badge badge-danger'
        default:
          return 'badge badge-light'
      }
    },

    getStatusText(status) {
      switch (status) {
        case 'A':
          return 'Ativa'
        case 'C':
          return 'Cancelada'
        case 'D':
          return 'Deferida'
        case 'I':
          return 'Indeferida'
        default:
          return status || '-'
      }
    },

    getSituacaoClass(situacao) {
      if (!situacao) return 'badge badge-light'
      
      const situacaoLower = situacao.toLowerCase()
      
      // Cancelada
      if (situacaoLower.includes('cancelada')) {
        return 'badge badge-secondary'
      }
      
      // Indeferida - verificar ANTES de "deferida" para evitar conflito
      if (situacaoLower.includes('indeferida')) {
        return 'badge badge-danger'
      }
      
      // Deferida - apenas estas devem ter verde
      if (situacaoLower.includes('deferida')) {
        return 'badge badge-success'
      }
      
      // Pagas ou com boleto pago (mas não deferida)
      if (situacaoLower.includes('boleto pago') || situacaoLower.includes('pago')) {
        return 'badge badge-success'
      }
      
      // Boleto emitido
      if (situacaoLower.includes('boleto emitido')) {
        return 'badge badge-info'
      }
      
      // Boleto vencido
      if (situacaoLower.includes('boleto vencido')) {
        return 'badge badge-warning'
      }
      
      // Isenção pendente
      if (situacaoLower.includes('isenção pendente') || situacaoLower.includes('isencao pendente')) {
        return 'badge badge-warning'
      }
      
      // Sem boleto emitido ou pendente
      if (situacaoLower.includes('sem boleto emitido') || situacaoLower.includes('pendente')) {
        return 'badge badge-warning'
      }
      
      // Ativa (sem mais informações)
      if (situacaoLower.includes('ativa')) {
        return 'badge badge-light'
      }
      
      // Default
      return 'badge badge-light'
    }
  }
}
</script>

<style lang="scss" scoped>
.inscricoes-lista {
  .page-header {
    background: white;
    border-radius: 15px;
    padding: 25px 30px;
    margin-bottom: 20px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);

    .page-title {
      margin: 0 0 8px 0;
      font-size: 24px;
      font-weight: 600;
      color: #2c3e50;
      display: flex;
      align-items: center;
      gap: 10px;

      i {
        color: #667eea;
      }
    }

    .page-subtitle {
      margin: 0;
      font-size: 14px;
      color: #6c757d;
    }
  }

  .filters-card {
    background: white;
    border-radius: 15px;
    padding: 20px 30px;
    margin-bottom: 20px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);

    .filters-row {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 20px;
      align-items: end;

      .filter-group {
        label {
          display: block;
          margin-bottom: 5px;
          font-weight: 600;
          color: #495057;
          font-size: 14px;
        }

        .form-control {
          border: 1px solid #e9ecef;
          border-radius: 8px;
          padding: 8px 12px;
          font-size: 14px;
          transition: border-color 0.3s ease;

          &:focus {
            outline: none;
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
          }
        }
      }

      .filter-actions {
        display: flex;
        gap: 10px;
        grid-column: 1 / -1;
        justify-content: center;
        margin-top: 10px;

        .btn {
          padding: 8px 16px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;

          &.btn-secondary {
            background: #6c757d;
            border-color: #6c757d;
            color: white;

            &:hover {
              background: #5a6268;
              border-color: #5a6268;
            }
          }

          &.btn-success {
            background: #28a745;
            border-color: #28a745;
            color: white;

            &:hover {
              background: #218838;
              border-color: #1e7e34;
            }
          }
        }
      }
    }
  }

  .stats-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    margin-bottom: 20px;

    .stat-card {
      background: white;
      border-radius: 15px;
      padding: 25px;
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
      border: 1px solid rgba(0, 0, 0, 0.05);
      display: flex;
      align-items: center;
      gap: 20px;

      .stat-icon {
        width: 60px;
        height: 60px;
        border-radius: 50%;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 24px;
      }

      .stat-content {
        .stat-value {
          font-size: 28px;
          font-weight: 700;
          color: #2c3e50;
          margin-bottom: 5px;
        }

        .stat-label {
          font-size: 14px;
          color: #6c757d;
          font-weight: 500;
        }
      }
    }
  }

  .table-card {
    background: white;
    border-radius: 15px;
    padding: 0;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);
    overflow: hidden;

    .loading-state, .empty-state {
      padding: 60px 30px;
      text-align: center;

      i {
        font-size: 48px;
        color: #667eea;
        margin-bottom: 15px;
        opacity: 0.7;
      }

      p, h3 {
        margin: 0;
        color: #6c757d;
      }

      h3 {
        font-size: 20px;
        font-weight: 600;
        margin-bottom: 8px;
        color: #495057;
      }
    }

    .table-responsive {
      overflow-x: auto;
    }

    .table {
      margin: 0;
      border-collapse: separate;
      border-spacing: 0;

      thead {
        background: #f8f9fa;

        th {
          padding: 15px 12px;
          font-weight: 600;
          color: #495057;
          border-bottom: 2px solid #e9ecef;
          font-size: 14px;
          white-space: nowrap;

          &.sortable {
            cursor: pointer;
            user-select: none;
            transition: background-color 0.3s ease;

            &:hover {
              background: #e9ecef;
            }

            i {
              margin-left: 5px;
              opacity: 0.5;
            }
          }

          &.col-id { width: 60px; }
          &.col-candidato { width: 200px; }
          &.col-cpf { width: 130px; }
          &.col-concurso { width: 180px; max-width: 180px; }
          &.col-cargo { width: 150px; }
          &.col-data { width: 110px; }
          &.col-status { width: 90px; }
          &.col-situacao { width: 120px; }
          &.col-acoes { width: 100px; text-align: center; }
        }
      }

      tbody {
        tr {
          transition: background-color 0.3s ease;

          &:hover { background: #f8f9fa; }

          td {
            padding: 12px;
            border-bottom: 1px solid #e9ecef;
            vertical-align: middle;
            font-size: 14px;

            .candidate-info {
              strong {
                color: #2c3e50;
                display: block;
                margin-bottom: 2px;
              }

              small {
                color: #6c757d;
                font-size: 12px;
                display: block;
              }
            }

            .concurso-info {
              max-width: 165px;
              
              .concurso-text {
                color: #2c3e50;
                font-weight: 500;
                display: block;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                cursor: help;
                transition: color 0.2s ease;

                &:hover { color: #667eea; }
              }
            }

            .badge {
              font-size: 12px;
              padding: 4px 8px;
              border-radius: 12px;
              font-weight: 500;

              &.badge-success { background: #28a745; color: white; }
              &.badge-secondary { background: #6c757d; color: white; }
              &.badge-warning { background: #ffc107; color: #212529; }
              &.badge-danger { background: #dc3545; color: white; }
              &.badge-info { background: #17a2b8; color: white; }
              &.badge-light { background: #f8f9fa; color: #495057; border: 1px solid #e9ecef; }
            }

            &.actions {
              text-align: center;
              white-space: nowrap;

              .btn {
                margin: 0 2px;
                padding: 6px 10px;
                border-radius: 6px;
                font-size: 12px;
                border: none;
                transition: all 0.3s ease;

                &.btn-info {
                  background: #17a2b8;
                  color: white;
                  &:hover { background: #138496; }
                }

                &.btn-warning {
                  background: #ffc107;
                  color: #212529;
                  &:hover { background: #e0a800; }
                }

                &.btn-success {
                  background: #28a745;
                  color: white;
                  &:hover { background: #218838; }
                }
              }
            }
          }
        }
      }
    }

    .pagination-wrapper {
      padding: 20px 30px;
      border-top: 1px solid #e9ecef;
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: #f8f9fa;

      .pagination {
        margin: 0;
        display: flex;
        gap: 5px;

        .page-item {
          .page-link {
            padding: 8px 12px;
            border: 1px solid #e9ecef;
            color: #667eea;
            background: white;
            border-radius: 6px;
            transition: all 0.3s ease;

            &:hover {
              background: #667eea;
              color: white;
              border-color: #667eea;
            }
          }

          &.active .page-link {
            background: #667eea;
            color: white;
            border-color: #667eea;
          }

          &.disabled .page-link {
            color: #6c757d;
            background: #f8f9fa;
            cursor: not-allowed;

            &:hover {
              background: #f8f9fa;
              color: #6c757d;
              border-color: #e9ecef;
            }
          }
        }
      }

      .pagination-info {
        font-size: 14px;
        color: #6c757d;
      }
    }
  }

  /* ===== Modal Styles (GLOBAL — fora do @media) ===== */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
    padding: 20px;
  }

  .modal-content {
    background: white;
    border-radius: 15px;
    width: 100%;
    max-width: 600px;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);

    &.large { max-width: 900px; }

    .modal-header {
      padding: 20px 25px;
      border-bottom: 1px solid #e9ecef;
      display: flex;
      align-items: center;
      justify-content: space-between;
      background: #f8f9fa;

      .modal-title {
        margin: 0;
        font-size: 18px;
        font-weight: 600;
        color: #2c3e50;
        display: flex;
        align-items: center;
        gap: 10px;

        i { color: #667eea; }
      }

      .modal-close {
        background: none;
        border: none;
        font-size: 20px;
        color: #6c757d;
        cursor: pointer;
        padding: 0;
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        transition: all 0.3s ease;

        &:hover {
          background: #e9ecef;
          color: #495057;
        }
      }
    }

    .modal-body {
      padding: 25px;

      .details-section {
        margin-bottom: 25px;
        padding-bottom: 25px;
        border-bottom: 1px solid #e9ecef;

        &:last-child {
          border-bottom: none;
          margin-bottom: 0;
          padding-bottom: 0;
        }

        .section-title {
          margin: 0 0 15px 0;
          font-size: 16px;
          font-weight: 600;
          color: #495057;
          display: flex;
          align-items: center;
          gap: 8px;

          i {
            color: #667eea;
            font-size: 14px;
          }
        }

        .details-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 15px;

          .detail-item {
            &.full-width { grid-column: 1 / -1; }

            label {
              display: block;
              font-weight: 600;
              color: #6c757d;
              font-size: 13px;
              margin-bottom: 5px;
            }

            span {
              display: block;
              color: #2c3e50;
              font-size: 14px;
            }

            .link-file {
              color: #667eea;
              text-decoration: none;
              display: inline-flex;
              align-items: center;
              gap: 8px;
              font-size: 14px;
              padding: 10px 15px;
              background: #f0f3ff;
              border-radius: 8px;
              transition: all 0.3s ease;

              &.large { font-size: 15px; padding: 12px 20px; }

              &:hover {
                background: #667eea;
                color: white;
                transform: translateY(-2px);
                box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
              }

              i { font-size: 14px; }
            }
          }
        }
      }
    }

    .modal-footer {
      padding: 15px 25px;
      border-top: 1px solid #e9ecef;
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      background: #f8f9fa;

      .btn {
        padding: 8px 16px;
        border: none;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.3s ease;

        &.btn-secondary {
          background: #6c757d;
          color: white;

          &:hover { background: #5a6268; }
        }
      }
    }
  }
}

/* ===== Responsividade ===== */
@media (max-width: 768px) {
  .inscricoes-lista {
    .filters-card .filters-row { grid-template-columns: 1fr; }
    .stats-cards { grid-template-columns: 1fr; }
    .table-card .pagination-wrapper {
      flex-direction: column;
      gap: 15px;
      text-align: center;
    }
  }
}
</style>
